import { makeObservable, observable, action, flow, computed } from "mobx";
import BaseDriveStore from "../Base/BaseDriveStore";
import { DocumentStore } from "../Base/DocumentStore";
import { FolderStore } from "../Base/FolderStore";
import { RenameModalStore } from "../RenameModal";
import { listFoldersByTenant } from "../../../../../services/folders";
import { listDocumentsByTenant, deleteDocumentByTenant } from "../../../../../services/document";

class DriveWorkspaceStore extends BaseDriveStore {
  currentPage = 'list'; // 'list' | 'upload'
  viewMode = 'grid'; // 'card' | 'table'
  fileInputRef = null; // Referencia al input file

  // Pagination state
  documentsCurrentPage = 1;
  documentsHasMore = false;
  isLoadingMoreDocuments = false;
  foldersCurrentPage = 1;
  foldersHasMore = false;
  isLoadingMoreFolders = false;

  // Share modal state
  isShareModalOpen = false;
  itemsToShare = [];
  vaultUuid = null; // ✅ Set from constructor parameter
  currentSelectionType = null; // 'document' | 'folder' | null
  isLoadingSharing = false;

  // Share form state
  selectedUsers = [];  // Siempre debe ser un array
  selectedPermission = '';
  pendingGrantees = []; // Lista de usuarios pendientes por compartir

  // Duplicate modal state
  isDuplicateModalOpen = false;
  itemToDuplicate = null;

  // Assign agent modal state
  isAssignAgentModalOpen = false;
  itemsToAssignAgent = [];
  selectedAgents = [];
  pendingAgents = [];
  currentAgents = [];
  isLoadingAgents = false;
  isLoadingAssign = false;

  // Move modal state
  isMoveModalOpen = false;
  itemIdBeingMoved = null;
  itemTypeBeingMoved = null; // 'folder' | 'document'

  // Delete modal state
  isDeleteModalOpen = false;
  documentToDelete = null;
  isDeletingDocument = false;
  deleteError = null;

  
  constructor({ foldersStore, documentsStore, agentsStore, resourcesStore, usersStore, selectionStore, vaultUuid }) {
    super({ foldersStore, documentsStore, agentsStore, selectionStore });
    this.resourcesStore = resourcesStore;
    this.usersStore = usersStore;
    this.vaultUuid = vaultUuid; // ✅ Set vaultUuid from parameter
    
    this.renameDocumentModalStore = new RenameModalStore();
    this.renameFolderModalStore = new RenameModalStore();
    
    // Initialize arrays
    this.selectedAgents = [];
    this.pendingAgents = [];
    this.currentAgents = [];
    
    this.setupSelectionSync();

    makeObservable(this, {
      currentPage: observable,
      viewMode: observable,
      fileInputRef: observable,
      // Pagination
      documentsCurrentPage: observable,
      documentsHasMore: observable,
      isLoadingMoreDocuments: observable,
      foldersCurrentPage: observable,
      foldersHasMore: observable,
      isLoadingMoreFolders: observable,
      // Share modal
      isShareModalOpen: observable,
      itemsToShare: observable,
      vaultUuid: observable,
      currentSelectionType: observable,
      selectedUsers: observable,
      selectedPermission: observable,
      isLoadingSharing: observable,
      pendingGrantees: observable,
      // Duplicate modal
      isDuplicateModalOpen: observable,
      itemToDuplicate: observable,
      // rename modals
      openRenameDocumentModal: action,
      openRenameFolderModal: action,

      // Assign agent modal
      isAssignAgentModalOpen: observable,
      itemsToAssignAgent: observable,
      selectedAgents: observable,
      pendingAgents: observable,
      currentAgents: observable,
      isLoadingAgents: observable,
      isLoadingAssign: observable,

      // Move modal
      isMoveModalOpen: observable,
      itemIdBeingMoved: observable,
      itemTypeBeingMoved: observable,
      
      // Actions
      setViewMode: action,
      setFileInputRef: action,
      openFileSelector: action,
      loadWorkspaceData: flow,
      loadMoreDocuments: flow,
      loadMoreFolders: flow,
      openShareModal: action,
      closeShareModal: action,
      loadResourcesForSharing: flow,
      removeAccessFromItem: flow,
      updatePermissionForGrantee: flow,
      setSelectedUsers: action,
      setSelectedPermission: action,
      addToPendingGrantees: action,
      removePendingGrantee: action,
      updatePendingGranteePermission: action,
      shareAllPendingGrantees: flow,
      openDuplicateModal: action,
      closeDuplicateModal: action,
      duplicateItem: flow,
      
      // Assign agent actions
      openAssignAgentModal: action,
      closeAssignAgentModal: action,
      setSelectedAgents: action,
      addToPendingAgents: action,
      removePendingAgent: action,
      removeAgent: action,
      assignAgentsToDocument: flow,

      // Move folder actions
      openMoveModal: action,
      closeMoveModal: action,

      // Delete document actions
      isDeleteModalOpen: observable,
      documentToDelete: observable,
      isDeletingDocument: observable,
      deleteError: observable,
      openDeleteModal: action,
      closeDeleteModal: action,
      deleteDocument: flow,
      removeDocumentFromList: action,
      extractErrorMessage: action,
      
      // Computed
      shouldShowGranteesList: computed,
      isMultipleItemsSharing: computed,
      sharingEntityType: computed,
      canShare: computed,
      duplicateModalTitle: computed,
      duplicateModalDescription: computed,
      createFolder: action
    });
  }


  openRenameDocumentModal = (document) => {
    this.renameDocumentModalStore.open({
      type: 'document',
      defaultValue: document.name,
      resourceId: document.id,
      onConfirm: ({ name, resourceId }) => {
        const doc = this.documentList.find(d => d.id === resourceId);
        if (doc) {
          doc.setName(name);
          this.setDocumentInList(doc);
        }
        this.renameDocumentModalStore.close();
      }
    });
  };

  openRenameFolderModal = (folder) => {
    this.renameFolderModalStore.open({
      type: 'folder',
      defaultValue: folder.name,
      resourceId: folder.id,
      onConfirm: ({ name, resourceId }) => {
        const fol = this.folderList.find(f => f.id === resourceId);
        if (fol) {
          fol.setName(name);
          this.setFolderInList(fol);
        }
        this.renameFolderModalStore.close();
      }
    });
  };

  setDocumentInList(document) {
      const documentIndex = this.documentList.findIndex(doc => doc.id === document.id);
      this.documentList[documentIndex] = document;
  }

  setFolderInList(folder) {
    const foldertIndex = this.folderList.findIndex(fol => fol.id === folder.id);
    this.folderList[foldertIndex] = folder;
  }

  setCurrentPage = (page) => {
    this.currentPage = page;
  };

  navigateToUpload = () => {
    this.setCurrentPage('upload');
  };

  navigateToList = () => {
    this.setCurrentPage('list');
  };

  setViewMode = (mode) => {
    this.viewMode = mode;
  };

  setFileInputRef = (ref) => {
    this.fileInputRef = ref;
  };

  openFileSelector = () => {
    if (this.fileInputRef?.current) {
      this.fileInputRef.current.click();
    }
  };

  // ✅ Load Data Actions - Uses vaultUuid from constructor
  *loadWorkspaceData() {
    try {
      this.isLoading = true;
      this.error = null;

      // ✅ Validate vaultUuid is set
      if (!this.vaultUuid) {
        console.warn('No vaultUuid available. Cannot load workspace data.');
        this.isLoading = false;
        return;
      }

      const foldersBody = {
        parent_folder_uuid: null,
        vault_uuid: this.vaultUuid, // ✅ Use vaultUuid from constructor
        page: 1,
        page_size: 8,
        order_by: ["updated_at"],
        order_direction: ["DESC"],
        filters: {},
        cursor: null
      };

      const documentsBody = {
        vault_uuid: this.vaultUuid, // ✅ Use vaultUuid from constructor
        page: 1,
        page_size: 8
      };

      // Hacer ambos llamados en paralelo
      const [foldersResponse, documentsResponse] = yield Promise.all([
        listFoldersByTenant(foldersBody),
        listDocumentsByTenant(documentsBody)
      ]);

      // Actualizar las listas con las respuestas
      if (foldersResponse?.data?.body) {
        this.setFolderList(foldersResponse.data.body || []);
        // Guardar información de paginación
        this.foldersCurrentPage = 1;
        this.foldersHasMore = foldersResponse.data.meta?.hasNext || false;
      }

      if (documentsResponse?.data?.body) {
        this.setDocumentList(documentsResponse.data.body || []);
        // Guardar información de paginación
        this.documentsCurrentPage = 1;
        this.documentsHasMore = documentsResponse.data.meta?.hasNext || false;
      }
    } catch (error) {
      this.error = error;
      console.error('Error loading workspace data:', error);
    } finally {
      this.isLoading = false;
    }
  }

  // Load more documents (siguiente página)
  *loadMoreDocuments() {
    if (!this.documentsHasMore || this.isLoadingMoreDocuments) {
      return;
    }

    try {
      this.isLoadingMoreDocuments = true;
      
      // Incrementar la página
      const nextPage = this.documentsCurrentPage + 1;

      const documentsBody = {
        vault_uuid: this.vaultUuid, // ✅ Use vaultUuid from constructor
        page: nextPage,
        page_size: 8
      };

      const documentsResponse = yield listDocumentsByTenant(documentsBody);

      if (documentsResponse?.data?.body) {
        // AGREGAR a la lista existente, no reemplazar
        const newDocuments = documentsResponse.data.body || [];
        
        // Convertir solo los nuevos documentos a DocumentStore (mismo formato que setDocumentList)
        const newDocumentStores = newDocuments.map((data) => new DocumentStore({
          id: data.id,
          title: data.title,
          lastUpdated: data.lastUpdated,
          lastUpdatedTooltip: data.lastUpdatedTooltip,
          folderCount: data.folderCount,
          owner: data.owner,
          date: data.date,
          status: data.status,
          tags: data.tags,
          document_uuid: data.document_uuid || data.documentUuid,
          vault_uuid: data.vault_uuid || data.vaultUuid,
          name: data.name,
          created_at: data.created_at || data.createdAt,
          updated_at: data.updated_at || data.updatedAt,
          description: data.description,
          active: data.active,
          version: data.version,
          scope: data.scope,
          summary: data.summary,
          path: data.path,
          owner_uuid: data.owner_uuid || data.ownerUuid,
          owner_full_name: data.owner_full_name || data.ownerFullName
        }));
        
        // Crear un nuevo array para que React detecte el cambio
        this.documentList = [...this.documentList, ...newDocumentStores];
        
        // Actualizar información de paginación
        this.documentsCurrentPage = nextPage;
        this.documentsHasMore = documentsResponse.data.meta?.hasNext || false;
      }
    } catch (error) {
      console.error('Error loading more documents:', error);
    } finally {
      this.isLoadingMoreDocuments = false;
    }
  }

  // Load more folders (siguiente página)
  *loadMoreFolders() {
    if (!this.foldersHasMore || this.isLoadingMoreFolders) {
      return;
    }

    try {
      this.isLoadingMoreFolders = true;
      
      // Incrementar la página
      const nextPage = this.foldersCurrentPage + 1;

      const foldersBody = {
        parent_folder_uuid: null,
        vault_uuid: this.vaultUuid, // ✅ Use vaultUuid from constructor
        page: nextPage,
        page_size: 8,
        order_by: ["updated_at"],
        order_direction: ["DESC"],
        filters: {},
        cursor: null
      };

      const foldersResponse = yield listFoldersByTenant(foldersBody);

      if (foldersResponse?.data?.body) {
        // AGREGAR a la lista existente, no reemplazar
        const newFolders = foldersResponse.data.body || [];
        
        // Convertir solo los nuevos folders a FolderStore (mismo formato que setFolderList)
        const newFolderStores = newFolders.map((data) => new FolderStore({
          id: data.id,
          title: data.title,
          lastUpdated: data.lastUpdated,
          lastUpdatedTooltip: data.lastUpdatedTooltip,
          folderCount: data.folderCount,
          folder_uuid: data.folder_uuid || data.folderUuid,
          name: data.name,
          internal_name: data.internal_name,
          path: data.path,
          is_active: data.is_active,
          created_at: data.created_at || data.createdAt,
          updated_at: data.updated_at || data.updatedAt,
          parent_folder_uuid: data.parent_folder_uuid || data.parentFolderUuid,
          sharedCountWith: data.sharedCountWith || data.shared_count_with || 0
        }));
        
        // Crear un nuevo array para que React detecte el cambio
        this.folderList = [...this.folderList, ...newFolderStores];
        
        // Actualizar información de paginación
        this.foldersCurrentPage = nextPage;
        this.foldersHasMore = foldersResponse.data.meta?.hasNext || false;
      }
    } catch (error) {
      console.error('Error loading more folders:', error);
    } finally {
      this.isLoadingMoreFolders = false;
    }
  }


  openShareModal = (item, type) => {
    // Reset state
    this.itemsToShare = [];
    this.currentSelectionType = null;
    
    // Primero verificar si se pasó un item específico
    if (item && type) {
      this.itemsToShare = [{ ...item, type }];
      this.currentSelectionType = type;
    } else if (this.selectionStore.selectedFolders.length > 0 || this.selectionStore.selectedDocuments.length > 0) {
      // Si no se pasó un item, usar los elementos seleccionados
      const hasSelectedFolders = this.selectionStore.selectedFolders.length > 0;
      const hasSelectedDocuments = this.selectionStore.selectedDocuments.length > 0;
      
      if (hasSelectedFolders && hasSelectedDocuments) {
        return {
          success: false,
          title: 'Invalid Selection',
          message: 'Cannot share folders and documents at the same time. Please select only folders or only documents.',
          variant: 'warning'
        };
      }
      
      if (hasSelectedFolders) {
        const selectedFolderItems = this.folderList
          .filter(folder => this.selectionStore.selectedFolders.includes(folder.folder_uuid))
          .map(folder => ({ ...folder, type: 'folder' }));        
        this.itemsToShare = selectedFolderItems;
        this.currentSelectionType = 'folder';
      }
      
      if (hasSelectedDocuments) {
        const selectedDocumentItems = this.documentList
          .filter(doc => this.selectionStore.selectedDocuments.includes(doc.document_uuid))
          .map(doc => ({ ...doc, type: 'document' }));
        this.itemsToShare = selectedDocumentItems;
        this.currentSelectionType = 'document';
      }
    }
    
    if (this.itemsToShare.length > 0) {
      this.isShareModalOpen = true;
      // Load resources data for sharing
      this.loadResourcesForSharing();
    }
  };

  closeShareModal = () => {
    this.isShareModalOpen = false;
    this.itemsToShare = [];
    this.selectedUsers = [];
    this.selectedPermission = '';
    this.currentSelectionType = null;
    this.pendingGrantees = []; // Limpiar la lista de pendientes
    // Clear resources data
    this.resourcesStore.clearGrantees();
    this.resourcesStore.clearPrivileges();
  };

  // Método para agregar usuarios a la lista de pendientes
  addToPendingGrantees = () => {
    if (this.selectedUsers.length === 0 || !this.selectedPermission) {
      return { 
        success: false,
        title: 'Invalid Selection',
        message: 'Please select users and permission',
        variant: 'warning'
      };
    }

    try {
      // Crear nuevos grantees desde los objetos de usuario
      const newGrantees = this.selectedUsers.map(userObj => ({
        grantee_uuid: userObj.user_uuid,
        grantee_name: userObj.full_name,
        grantee_type: 'user',
        grantee_email: userObj.email || '',
        privileges: [{
          privilege_uuid: `temp-priv-${Date.now()}`,
          privilege_name: this.selectedPermission
        }]
      }));

      // Agregar a la lista existente
      this.pendingGrantees = [...this.pendingGrantees, ...newGrantees];

      // Reset form
      this.selectedUsers = [];
      this.selectedPermission = '';
      
    } catch (error) {
      console.error('Error adding users to pending list:', error);
      return { 
        success: false, 
        message: error.message || 'An unexpected error occurred',
        variant: 'error'
      };
    }
  };

  // Método para remover un grantee pendiente
  removePendingGrantee = (granteeUuid) => {
    this.pendingGrantees = this.pendingGrantees.filter(
      grantee => grantee.grantee_uuid !== granteeUuid
    );
  };

  // Método para actualizar el permiso de un grantee pendiente
  updatePendingGranteePermission = (granteeUuid, newPermission) => {
    this.pendingGrantees = this.pendingGrantees.map(grantee => {
      if (grantee.grantee_uuid === granteeUuid) {
        return {
          ...grantee,
          privileges: [{
            ...grantee.privileges[0],
            privilege_name: newPermission
          }]
        };
      }
      return grantee;
    });
  };

  // Form state setters
  setSelectedUsers = (users) => {
    // Asegurarnos de que siempre sea un array
    this.selectedUsers = Array.isArray(users) ? users : [];
  };

  setSelectedPermission = (permission) => {
    this.selectedPermission = permission;
  };

  // Load resources data when opening share modal
  *loadResourcesForSharing() {
    if (!this.currentSelectionType) return;

    // ✅ LAZY LOAD: Load privileges and users only when Share modal opens (with memoization)
    yield Promise.all([
      this.resourcesStore.getFolderPrivileges(),
      this.resourcesStore.getDocumentPrivileges(),
      this.usersStore.fetchUsersFilters()
    ]);

    const entityType = this.currentSelectionType === 'folder' ? 'FOLDER' : 'DOCUMENT';
    
    if (this.itemsToShare.length === 1) {
      const item = this.itemsToShare[0];
      const entityUuid = this.currentSelectionType === 'folder' ? item.folder_uuid : item.document_uuid;
      
      // Load current grantees (who has access)
      yield this.resourcesStore.loadGrantees(entityType, entityUuid);
    }
  }

  // Dar acceso a todos los usuarios pendientes
  *shareAllPendingGrantees() {
    if (this.pendingGrantees.length === 0) {
      return {
        success: false,
        title: 'No Users Selected',
        message: 'Please add users to share with',
        variant: 'warning'
      };
    }

    try {
      this.isLoadingSharing = true;

      // Agrupar usuarios por privilegio para optimizar las llamadas
      const privilegeGroups = {};
      this.pendingGrantees.forEach(grantee => {
        const privilege = grantee.privileges[0].privilege_name;
        if (!privilegeGroups[privilege]) {
          privilegeGroups[privilege] = [];
        }
        privilegeGroups[privilege].push(grantee.grantee_uuid);
      });

      // Realizar llamadas por cada grupo de privilegios
      for (const [privilege, userUuids] of Object.entries(privilegeGroups)) {
        const privilegeUuids = this.resourcesStore[this.currentSelectionType === 'folder' ? 'folderPrivileges' : 'documentPrivileges']
          .filter(p => p.privilegeName === privilege)
          .map(p => p.entityPrivilegeUuid);

        if (this.isMultipleItemsSharing) {
          // Para múltiples items
          for (const item of this.itemsToShare) {
            const entityType = item.type === 'folder' ? 'FOLDER' : 'DOCUMENT';
            const entityUuid = item.type === 'folder' ? item.folder_uuid : item.document_uuid;
            yield this.resourcesStore.addGrant(entityType, entityUuid, privilegeUuids, userUuids, []);
          }
        } else {
          // Para un solo item
          const item = this.itemsToShare[0];
          const entityType = item.type === 'folder' ? 'FOLDER' : 'DOCUMENT';
          const entityUuid = item.type === 'folder' ? item.folder_uuid : item.document_uuid;
          yield this.resourcesStore.addGrant(entityType, entityUuid, privilegeUuids, userUuids, []);
        }
      }

      // Cerrar modal y limpiar estado
      this.closeShareModal();
      
      return { 
        success: true, 
        title: 'Success',
        message: 'Access granted successfully to all users',
        variant: 'success'
      };
    } catch (error) {
      console.error('Error sharing with users:', error);
      this.isLoadingSharing = false;
      return { 
        success: false, 
        message: error.response?.data?.msg_code 
          ? error.response.data.msg_code.map(msg => msg.description).join(', ')
          : error.message || 'An unexpected error occurred',
        variant: 'error'
      };
    } finally {
      this.isLoadingSharing = false;
    }
  }

  // Remove access from a grantee
  *removeAccessFromItem(granteeUuid, granteeType) {
    try {
      const item = this.itemsToShare[0];
      // Use correct entity types from API spec
      const entityType = item.type === 'folder' ? 'VAULT' : 'DOCUMENT';
      const entityUuid = item.type === 'folder' ? item.folder_uuid : item.document_uuid;

      const users = granteeType === 'user' ? [granteeUuid] : [];
      const groups = granteeType === 'group' ? [granteeUuid] : [];

      yield this.resourcesStore.removeGrant(entityType, entityUuid, users, groups);
      return { 
        success: true, 
        title: 'Success',
        message: 'Access removed successfully',
        variant: 'success'
      };
    } catch (error) {
      console.error('Error removing access:', error);
      return { 
        success: false,
        title: 'Error',
        message: error.response?.data?.msgCode 
          ? error.response.data.msgCode.map(msg => msg.description).join(', ')
          : error.message || 'An unexpected error occurred',
        variant: 'error'
      };
    }
  }

  // Update permission for a grantee
  *updatePermissionForGrantee(granteeUuid, granteeType, newPermission) {
    try {
      const item = this.itemsToShare[0];
      // Use correct entity types from API spec
      const entityType = item.type === 'folder' ? 'VAULT' : 'DOCUMENT';
      const entityUuid = item.type === 'folder' ? item.folder_uuid : item.document_uuid;

      const privilegeUuids = this.resourcesStore.privileges
        .filter(p => p.privilegeName === newPermission)
        .map(p => p.entityPrivilegeUuid);

      const users = granteeType === 'user' ? [granteeUuid] : [];
      const groups = granteeType === 'group' ? [granteeUuid] : [];

      yield this.resourcesStore.updateGrant(entityType, entityUuid, privilegeUuids, users, groups);
      return { 
        success: true,
        title: 'Success',
        message: 'Permission updated successfully',
        variant: 'success'
      };
    } catch (error) {
      console.error('Error updating permission:', error);
      return { 
        success: false,
        title: 'Error',
        message: error.response?.data?.msgCode 
          ? error.response.data.msgCode.map(msg => msg.description).join(', ')
          : error.message || 'An unexpected error occurred',
        variant: 'error'
      };
    }
  }

  // Duplicate Modal Actions
  openDuplicateModal = (item, type) => {
    this.itemToDuplicate = { ...item, type };
    this.isDuplicateModalOpen = true;
  };

  closeDuplicateModal = () => {
    this.isDuplicateModalOpen = false;
    this.itemToDuplicate = null;
  };

  *duplicateItem() {
    if (!this.itemToDuplicate) {
      console.warn('No item to duplicate');
      return { success: false };
    }

    try {
      if (this.itemToDuplicate.type === 'folder') {
        // Duplicate folder usando el endpoint específico
        const folderUuid = this.itemToDuplicate.folder_uuid;
        const duplicatedFolder = yield this.foldersStore.duplicateFolder(folderUuid);
        
        if (duplicatedFolder) {
          // Agregar directamente a la lista de folders sin recargar todo
          this.setFolderList([...this.folderList, duplicatedFolder]);
          this.closeDuplicateModal();
          return { 
            success: true, 
            data: duplicatedFolder,
            title: 'Success',
            message: `Folder "${this.itemToDuplicate.name}" duplicated successfully`,
            variant: 'success'
          };
        }
      } else if (this.itemToDuplicate.type === 'document') {
        // Duplicate document usando el endpoint específico
        const documentUuid = this.itemToDuplicate.document_uuid;
        const duplicatedDocument = yield this.documentsStore.duplicateDocument(documentUuid);
        
        if (duplicatedDocument) {
          // Agregar directamente a la lista de documents sin recargar todo
          this.setDocumentList([...this.documentList, duplicatedDocument]);
          this.closeDuplicateModal();
          return { 
            success: true, 
            data: duplicatedDocument,
            title: 'Success',
            message: `Document "${this.itemToDuplicate.name}" duplicated successfully`,
            variant: 'success'
          };
        }
      }

      return { 
        success: false,
        message: 'Unable to duplicate item. Please try again.',
        variant: 'error'
      };
    } catch (error) {
      console.error('Error duplicating item:', error);
      return { 
        success: false, 
        message: error.message || 'An unexpected error occurred while duplicating the item',
        variant: 'error'
      };
    }
  }

  // Delete Modal Actions
  openDeleteModal = (document) => {
    this.documentToDelete = document;
    this.isDeleteModalOpen = true;
  };

  closeDeleteModal = () => {
    this.isDeleteModalOpen = false;
    this.documentToDelete = null;
    this.deleteError = null;
  };

  *deleteDocument() {
    if (!this.documentToDelete) {
      return { success: false, error: 'No document selected for deletion' };
    }

    this.isDeletingDocument = true;
    this.deleteError = null;

    try {
      const documentUuid = this.documentToDelete.documentUuid || this.documentToDelete.document_uuid;

      if (!documentUuid) {
        throw new Error('Document UUID not found');
      }

      const response = yield deleteDocumentByTenant({ document_uuid: documentUuid });

      const responseData = response?.data || response;
      if (responseData?.statusCode && responseData.statusCode >= 400) {
        const errorMsg = this.extractErrorMessage(responseData);
        throw new Error(errorMsg);
      }

      this.removeDocumentFromList(documentUuid);

      return { 
        success: true,
        title: 'Success',
        message: `Document "${this.documentToDelete.name}" deleted successfully`,
        variant: 'success'
      };
    } catch (error) {
      console.error('Error deleting document:', error);
      const errorMessage = this.extractErrorMessage(error.response?.data || error.message || 'Error deleting document');
      this.deleteError = errorMessage;
      return { 
        success: false, 
        error: errorMessage,
        title: 'Error',
        message: errorMessage,
        variant: 'error'
      };
    } finally {
      this.isDeletingDocument = false;
    }
  }

  extractErrorMessage = (errorData) => {
    if (typeof errorData === 'string') {
      return errorData;
    }

    if (errorData?.msgCode && Array.isArray(errorData.msgCode) && errorData.msgCode.length > 0) {
      const firstError = errorData.msgCode[0];
      return firstError.description || firstError.code || 'Error deleting document';
    }

    if (errorData?.message) {
      return errorData.message;
    }

    if (errorData?.error) {
      return errorData.error;
    }

    return 'Error deleting document';
  }

  removeDocumentFromList = (documentUuid) => {
    this.documentList = this.documentList.filter(
      doc => (doc.document_uuid || doc.documentUuid || doc.id) !== documentUuid
    );
  };

  // Helper computed to check if we should show grantees list
  get shouldShowGranteesList() {
    return this.itemsToShare.length === 1;
  }
  
  // Helper computed properties for sharing
  get isMultipleItemsSharing() {
    return this.itemsToShare.length > 1;
  }

  get sharingEntityType() {
    return this.currentSelectionType === 'folder' ? 'VAULT' : 'DOCUMENT';
  }

  get canShare() {
    return this.currentSelectionType !== null && this.itemsToShare.length > 0;
  }

  // Computed properties for duplicate modal
  get duplicateModalTitle() {
    if (!this.itemToDuplicate) return '';
    return this.itemToDuplicate.type === 'folder' 
      ? 'Duplicate this folder?' 
      : 'Duplicate this document?';
  }

  get duplicateModalDescription() {
    if (!this.itemToDuplicate) return '';
    
    if (this.itemToDuplicate.type === 'folder') {
      return `${this.itemToDuplicate.name} will be copied to the current location.`;
    } else {
      return `An identical copy of ${this.itemToDuplicate.name} will be created. You can rename or edit it anytime.`;
    }
  }

  createFolder = async (folderData) => {
    const newFolder = await this.foldersStore.createFolder(folderData);
    
    // If folder was created successfully, reload the workspace data
    if (newFolder) {
      await this.loadWorkspaceData();
    }
    
    return newFolder;
  }

  // Assign agent methods
  openAssignAgentModal = (item) => {
    // Reset state
    this.itemsToAssignAgent = [];
    
    // Primero verificar si se pasó un item específico
    if (item) {
      this.itemsToAssignAgent = [{ ...item, type: 'document' }];
    } else if (this.selectionStore.selectedDocuments.length > 0) {
      // Si no se pasó un item, usar los documentos seleccionados
      const hasSelectedFolders = this.selectionStore.selectedFolders.length > 0;
      const hasSelectedDocuments = this.selectionStore.selectedDocuments.length > 0;
      
      if (hasSelectedFolders && hasSelectedDocuments) {
        return {
          success: false,
          title: 'Invalid Selection',
          message: 'Agent assignment is only available for documents. Please select only documents.',
          variant: 'warning'
        };
      }

      if (hasSelectedFolders && !hasSelectedDocuments) {
        return {
          success: false,
          title: 'Invalid Selection',
          message: 'Agent assignment is only available for documents, not folders.',
          variant: 'warning'
        };
      }
      
      if (hasSelectedDocuments) {
        const selectedDocumentItems = this.documentList
          .filter(doc => this.selectionStore.selectedDocuments.includes(doc.document_uuid))
          .map(doc => ({ ...doc, type: 'document' }));
        this.itemsToAssignAgent = selectedDocumentItems;
      }
    }
    
    if (this.itemsToAssignAgent.length > 0) {
      this.isAssignAgentModalOpen = true;
      // Cargar la lista de agentes si no está cargada
      if (this.agentsStore.agents.length === 0) {
        this.agentsStore.fetchAgents();
      }
    }
  };

  closeAssignAgentModal = () => {
    this.isAssignAgentModalOpen = false;
    this.itemsToAssignAgent = [];
    this.selectedAgents = [];
    this.pendingAgents = [];
  };

  setSelectedAgents = (agents) => {
    this.selectedAgents = agents;
  };

  addToPendingAgents = () => {
    if (this.selectedAgents.length === 0) {
      return {
        success: false,
        title: 'Invalid Selection',
        message: 'Please select agents to add',
        variant: 'warning'
      };
    }

    try {
      // Add selected agents to pending list
      this.pendingAgents = [...this.pendingAgents, ...this.selectedAgents];

      // Reset selection
      this.selectedAgents = [];

      return {
        success: true,
        title: 'Success',
        message: 'Agents added to the list',
        variant: 'success'
      };
    } catch (error) {
      console.error('Error adding agents to pending list:', error);
      return {
        success: false,
        message: error.message || 'An unexpected error occurred',
        variant: 'error'
      };
    }
  };

  removePendingAgent = (agentUuid) => {
    this.pendingAgents = this.pendingAgents.filter(
      agent => agent.uuid !== agentUuid
    );
  };

  removeAgent = (agentUuid) => {
    this.currentAgents = this.currentAgents.filter(
      agent => agent.uuid !== agentUuid
    );
  };

  *assignAgentsToDocument(userId) {
    if (this.itemsToAssignAgent.length === 0 || this.pendingAgents.length === 0) {
      return {
        success: false,
        title: 'Invalid Selection',
        message: 'Please add at least one agent to the list.',
        variant: 'warning'
      };
    }

    try {
      let successCount = 0;
      let errorMessages = [];

      this.isLoadingAssign = true;
      
      // Iterar por cada documento
      for (const document of this.itemsToAssignAgent) {
        // Iterar por cada agente
        for (const agent of this.pendingAgents) {
          const agentId = agent.uuid;
          try {
            const body = {
              document_uuid: document.document_uuid,
              agentUuid: agentId,
              expires_at: "2025-07-10T10:30:00Z", // TODO: Make this configurable
              owner: "user123", // TODO: Get from user context
              created_by: "user123", // TODO: Get from user context
              description: "Assign agent to document",
              granted_by: userId,
              privilege: "reader"
            };

            const response = yield this.agentsStore.assignDocumentAgent(body);
            if (response?.status === 200 || response?.status === 201) {
              successCount++;
            }
            this.isLoadingAssign=false;

          } catch (error) {
            const errorMessage = error.response?.data?.message || "Error assigning agent to document";
            this.isLoadingAssign = false;
            errorMessages.push(errorMessage);
          }
        }
      }

      this.closeAssignAgentModal();

      const documentCount = this.itemsToAssignAgent.length;
      const agentCount = this.pendingAgents.length;

      if (errorMessages.length === 0) {
        return {
          success: true,
          title: 'Success',
          message: `Successfully assigned ${agentCount} agent${agentCount !== 1 ? 's' : ''} to ${documentCount} document${documentCount !== 1 ? 's' : ''}.`,
          variant: 'success'
        };
      } else {
        return {
          success: false,
          title: 'Error',
          message: errorMessages.join('. '),
          variant: 'error'
        };
      }
    } catch (error) {
      console.error('Error assigning agents:', error);
      return {
        success: false,
        title: 'Error',
        message: error.message || 'An unexpected error occurred while assigning agents.',
        variant: 'error'
      };
    }
  }

  // Move modal methods
  openMoveModal = (itemId, itemType = 'folder') => {
    this.itemIdBeingMoved = itemId;
    this.itemTypeBeingMoved = itemType;
    this.isMoveModalOpen = true;
  };

  closeMoveModal = () => {
    this.isMoveModalOpen = false;
    this.itemIdBeingMoved = null;
    this.itemTypeBeingMoved = null;
  };

  // Setup automatic synchronization between SelectionStore and local isSelected states
  setupSelectionSync = () => {
    // Clean up existing callbacks first
    if (this.folderSyncCallback) {
      this.selectionStore.unregisterSyncCallback('folder', this.folderSyncCallback);
    }
    if (this.documentSyncCallback) {
      this.selectionStore.unregisterSyncCallback('document', this.documentSyncCallback);
    }

    // Folder sync callback
    this.folderSyncCallback = (selectedFolders) => {
      this.folderList.forEach(folder => {
        const shouldBeSelected = selectedFolders.includes(folder.id);
        if (folder.isSelected !== shouldBeSelected) {
          folder.setSelected(shouldBeSelected);
        }
      });
    };

    // Document sync callback
    this.documentSyncCallback = (selectedDocuments) => {
      this.documentList.forEach(document => {
        const shouldBeSelected = selectedDocuments.includes(document.id);
        if (document.isSelected !== shouldBeSelected) {
          document.setSelected(shouldBeSelected);
        }
      });
    };

    // Register callbacks
    this.selectionStore.registerSyncCallback('folder', this.folderSyncCallback);
    this.selectionStore.registerSyncCallback('document', this.documentSyncCallback);
  };
}

export default DriveWorkspaceStore;

